import { useState } from "react";
import { useUserpilot } from "@platform/hooks/useUserpilot";

import TrashIcon from "@assets/icons/trash.svg";
import EmailSyncIcon from "@assets/icons/email-sync-icon.svg";

import useEmailStore from "@store/useEmailStore";
import usePlatformStore from "@store/usePlatformStore";
import { useRemoveEmail } from "@screens/sync-email/hooks/useRemoveEmail";
import { useRemoveThread } from "@screens/sync-email/hooks/useRemoveThread";
import { useUnsubscribeThread } from "@screens/sync-email/hooks/useUnsubscribeThread";
import { IThreadResponse } from "@interfaces/thread.interface";
import { UnsubscribeOptions } from "@enums/common.enum";
import { UnsubscribeSection } from "@components/Sections";
import { Button } from "@ui/Button";
import { View } from "@ui/View";

interface Props {
  thread?: IThreadResponse;
}

export function ThreadHeader({ thread }: Props) {
  const { track } = useUserpilot();
  const { emailId, threadId } = useEmailStore();
  const { isComposeMode } = usePlatformStore();
  const { mutateAsync: removeEmailMutate, isPending: isRemoveEmailPending } = useRemoveEmail();
  const { mutateAsync: removeThreadMutate, isPending: isRemoveThreadPending } = useRemoveThread();
  const { mutateAsync: unsubscribeThreadMutate, isPending: isUnsubscribePending } =
    useUnsubscribeThread();

  const [showUnsubscribeSection, setShowUnsubscribeSection] = useState(false);

  const isThreadSubscribed = thread?.sync_thread || false;

  const unsubscribeFromThread = async () => {
    await unsubscribeThreadMutate({ threadId, body: { sync_thread: false } });
    track("Click: Unsubscribe from future emails");
  };

  const removeAllEmailsExceptCurrent = async () => {
    const body = { sync_thread: false, remove_all_emails_except: [emailId] };
    await unsubscribeThreadMutate({ threadId, body });
    track("Click: Keep this email only");
  };

  const removeFullThread = async () => {
    await removeThreadMutate();
    track("Click: Delete the whole thread");
  };

  const removeEmail = async () => {
    await removeEmailMutate();
    track("Click: Delete email");
  };

  const handleRemove = async () => {
    if (isThreadSubscribed) {
      handleShowUnsubscribeSection();
    } else {
      await removeEmail();
    }
  };

  const handleUnsubscribe = async (option: UnsubscribeOptions) => {
    switch (option) {
      case UnsubscribeOptions.UNSUBSCRIBE:
        await unsubscribeFromThread();
        break;
      case UnsubscribeOptions.KEEP_THIS_EMAIL_ONLY:
        await removeAllEmailsExceptCurrent();
        break;
      case UnsubscribeOptions.REMOVE_THREAD:
        await removeFullThread();
        break;
    }

    handleHideUnsubscribeSection();
  };

  const handleShowUnsubscribeSection = () => {
    setShowUnsubscribeSection(true);
  };

  const handleHideUnsubscribeSection = () => {
    setShowUnsubscribeSection(false);
  };

  const isPending =
    !emailId || isRemoveEmailPending || isRemoveThreadPending || isUnsubscribePending;

  let removeButtonText = isComposeMode
    ? "Cancel sync"
    : isThreadSubscribed
      ? "Unsubscribe and Delete"
      : "Remove Email";

  return (
    <header className="flex flex-col gap-4 pt-2 w-full">
      <div className="flex items-center justify-between gap-1">
        <div className="flex flex-col items-start gap-1">
          <EmailSyncIcon width={48} height={48} className="z-20" />
        </div>

        <Button
          buttonType={"SECONDARY"}
          onClick={handleRemove}
          className="w-auto min-w-[36px] h-[36px] px-3 flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-invertedPrimary80 focus:dark:bg-gray-600"
          style={isPending ? { visibility: "hidden" } : undefined}
        >
          {!isComposeMode && <TrashIcon />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {removeButtonText}
          </span>
        </Button>
      </div>

      <View.Condition if={showUnsubscribeSection}>
        <UnsubscribeSection
          isPending={isPending}
          onSubmit={handleUnsubscribe}
          onCancel={handleHideUnsubscribeSection}
        />
      </View.Condition>
    </header>
  );
}
